import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../utils/api';
import { ArrowLeft, Plus, Trash, UploadSimple, FloppyDisk, Trophy, Sparkle, CheckCircle, X, Crown, Medal, FilePdf, Eye, CaretDown, CaretRight } from '@phosphor-icons/react';

/**
 * Org-level Point System + Members admin page (v215).
 *
 * Sections:
 *  1. Point Table editor — producer sets `place → points` rows.
 *     v216: Load IBRA / NBHA presets as an editable draft (Save persists).
 *  2. Members roster — bulk CSV upload + individual add + edit start date.
 *  3. Season standings — computed rollup with membership_start_date gate.
 */
export default function OrgPointsAdmin() {
  const { orgId } = useParams();
  const [placings, setPlacings] = useState([]);
  const [savingPoints, setSavingPoints] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [divisionFormat, setDivisionFormat] = useState('4D');
  const [coSanctionRequests, setCoSanctionRequests] = useState([]);
  const [members, setMembers] = useState([]);
  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', email: '', phone: '', membership_start_date: '' });
  const [addFormOptOpen, setAddFormOptOpen] = useState(false);
  const [standings, setStandings] = useState(null);
  const [selectedBucket, setSelectedBucket] = useState('');
  const [year, setYear] = useState('');
  const csvRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [pts, mems, prs, reqs] = await Promise.all([
        api.get(`/organizations/${orgId}/point-system`),
        api.get(`/organizations/${orgId}/point-members`),
        api.get(`/organizations/point-system/presets`),
        api.get(`/organizations/${orgId}/co-sanction-requests`),
      ]);
      setPlacings(pts.data.placings || []);
      setDivisionFormat(pts.data.division_format || '4D');
      setMembers(mems.data || []);
      setPresets(prs.data.presets || []);
      setCoSanctionRequests(reqs.data || []);
    } catch (e) { toast.error('Failed to load'); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const loadStandings = async () => {
    try {
      const q = year ? `?year=${year}` : '';
      const r = await api.get(`/organizations/${orgId}/standings${q}`);
      setStandings(r.data);
      // Default to the first division that has any placings, else the
      // first division in the format.
      const buckets = r.data.buckets || [];
      const firstWithData = buckets.find((b) => (b.standings || []).length > 0);
      setSelectedBucket((firstWithData || buckets[0])?.division || '');
    } catch { toast.error('Failed to load standings'); }
  };

  const savePoints = async () => {
    setSavingPoints(true);
    try {
      await api.put(`/organizations/${orgId}/point-system`, {
        placings, division_format: divisionFormat,
      });
      toast.success('Point system saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingPoints(false); }
  };

  const addPlacing = () => {
    const next = placings.length ? (placings[placings.length - 1].place + 1) : 1;
    setPlacings([...placings, { place: next, points: 1 }]);
  };
  const removePlacing = (i) => setPlacings(placings.filter((_, x) => x !== i));
  const updatePlacing = (i, k, v) => {
    const copy = [...placings];
    copy[i] = { ...copy[i], [k]: k === 'place' ? parseInt(v, 10) || 0 : parseFloat(v) || 0 };
    setPlacings(copy);
  };

  const loadPreset = (key) => {
    setSelectedPreset(key);
    if (!key) return;
    const preset = presets.find((p) => p.key === key);
    if (!preset) return;
    // Deep-copy so edits don't mutate the preset in memory.
    setPlacings(preset.placings.map((r) => ({ ...r })));
    // v221 — each preset carries its natural division format (IBRA Open=4D,
    // IBRA YAM=3D, NBHA=4D, NBHA_5D=5D). Adopting the preset also adopts
    // the format so standings roll up correctly.
    if (preset.division_format) setDivisionFormat(preset.division_format);
    toast.success(`Loaded ${preset.label} (${preset.division_format || '4D'}) — click Save to apply.`);
  };

  const approveCoSanction = async (showId) => {
    try {
      await api.post(`/organizations/${orgId}/co-sanction-requests/${showId}/approve`);
      toast.success('Co-sanction approved');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
  };

  const rejectCoSanction = async (showId) => {
    if (!window.confirm('Reject this co-sanction request?')) return;
    try {
      await api.post(`/organizations/${orgId}/co-sanction-requests/${showId}/reject`);
      toast.success('Rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Reject failed'); }
  };

  const addMember = async () => {
    if (!addForm.first_name.trim() || !addForm.last_name.trim()) {
      toast.error('First + last name required'); return;
    }
    try {
      await api.post(`/organizations/${orgId}/point-members`, addForm);
      toast.success('Member added');
      setAddForm({ first_name: '', last_name: '', email: '', phone: '', membership_start_date: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
  };

  const deleteMember = async (id) => {
    if (!window.confirm('Delete this member?')) return;
    try { await api.delete(`/organizations/${orgId}/point-members/${id}`); load(); }
    catch { toast.error('Delete failed'); }
  };

  const updateStartDate = async (id, date) => {
    try {
      await api.patch(`/organizations/${orgId}/point-members/${id}`, { membership_start_date: date });
      toast.success('Updated');
      load();
    } catch { toast.error('Update failed'); }
  };

  const downloadStandingsPdf = async () => {
    try {
      const q = year ? `?year=${year}` : '';
      const r = await api.get(`/organizations/${orgId}/standings.pdf${q}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `standings${year ? `_${year}` : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF export failed'); }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post(`/organizations/${orgId}/point-members/bulk`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.created} added, ${r.data.updated} updated, ${r.data.skipped} skipped`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { if (csvRef.current) csvRef.current.value = ''; }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white p-4 sm:p-6" data-testid="org-points-admin-page">
      <Link to={`/dashboard`} className="inline-flex items-center gap-2 text-[#9E9E9E] hover:text-white text-sm mb-6"><ArrowLeft size={16} /> Back</Link>
      <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
        <h1 className="text-2xl sm:text-3xl font-bold" style={{fontFamily: 'Outfit'}}>Points & Members</h1>
        <Link
          to={`/organizations/${orgId}/standings`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#00D4FF]/15 text-[#00D4FF] border border-[#00D4FF]/30 rounded text-xs font-bold hover:bg-[#00D4FF]/25"
          data-testid="view-public-standings-link"
        >
          <Eye size={13} weight="bold" /> View Public Standings
        </Link>
      </div>
      <p className="text-[#9E9E9E] text-sm mb-8">Configure your organization&apos;s point system and membership roster.</p>

      {/* Pending Co-Sanction Requests — v217. Only rendered when there
          ARE pending requests so the UI stays clean for orgs that never
          get co-sanction requests. */}
      {coSanctionRequests.length > 0 && (
        <section className="bg-[#161616] border border-[#FF9500]/40 rounded-lg p-4 sm:p-6 mb-8" data-testid="co-sanction-requests-section">
          <h2 className="text-lg font-bold mb-1 text-[#FF9500]">
            Pending Co-Sanction Requests
            <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-[#FF9500] text-black text-xs font-bold rounded-full">
              {coSanctionRequests.length}
            </span>
          </h2>
          <p className="text-[#9E9E9E] text-xs mb-4">
            Other producers have requested that you co-sanction their shows.
            Approving a request means the show will count toward YOUR season
            standings using YOUR point table and member roster.
          </p>
          <div className="space-y-2">
            {coSanctionRequests.map((r) => (
              <div key={r.show_id} className="flex flex-wrap items-center gap-2 p-3 bg-[#0A0A0A] border border-[#262626] rounded" data-testid={`co-sanction-req-${r.show_id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="text-sm font-semibold text-white">{r.show_name}</div>
                  <div className="text-[11px] text-[#9E9E9E]">
                    {r.start_date} · {r.producer_org_name}
                    {r.location ? ` · ${r.location}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => approveCoSanction(r.show_id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 rounded text-xs font-bold hover:bg-[#34C759]/25"
                  data-testid={`co-sanction-approve-${r.show_id}`}
                >
                  <CheckCircle size={13} weight="bold" /> Approve
                </button>
                <button
                  onClick={() => rejectCoSanction(r.show_id)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/30 rounded text-xs font-bold hover:bg-[#FF3B30]/25"
                  data-testid={`co-sanction-reject-${r.show_id}`}
                >
                  <X size={13} weight="bold" /> Reject
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Point Table */}
      <section className="bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6 mb-8" data-testid="point-table-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Point Table</h2>
          <button onClick={savePoints} disabled={savingPoints} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#34C759]/15 text-[#34C759] border border-[#34C759]/30 rounded text-xs font-bold hover:bg-[#34C759]/25 disabled:opacity-40" data-testid="save-points-btn">
            <FloppyDisk size={13} weight="bold" /> {savingPoints ? 'Saving...' : 'Save'}
          </button>
        </div>
        {/* Preset selector — loads IBRA / NBHA templates as an editable draft. */}
        <div className="mb-4 p-3 bg-[#0A0A0A] border border-[#262626] rounded flex items-center gap-2 flex-wrap" data-testid="preset-selector">
          <Sparkle size={14} weight="fill" className="text-[#FF9500]" />
          <label className="text-xs text-[#9E9E9E] whitespace-nowrap">Load preset:</label>
          <select
            value={selectedPreset}
            onChange={(e) => loadPreset(e.target.value)}
            className="flex-1 min-w-[200px] px-2 py-1.5 bg-[#161616] border border-[#262626] rounded text-sm text-white"
            data-testid="preset-select"
          >
            <option value="">— Choose a preset —</option>
            {['IBRA', 'NBHA'].map((assoc) => (
              <optgroup key={assoc} label={assoc}>
                {presets.filter((p) => p.association === assoc).map((p) => (
                  <option key={p.key} value={p.key}>{p.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <span className="text-[10px] text-[#9E9E9E]">Loads into editor — click Save to persist.</span>
        </div>
        {/* v221 — org-wide division format. This drives how standings
            get bucketed (independent of any single class's own
            division_type). */}
        <div className="mb-4 p-3 bg-[#0A0A0A] border border-[#262626] rounded flex items-center gap-2 flex-wrap" data-testid="division-format-row">
          <label className="text-xs text-[#9E9E9E] whitespace-nowrap font-semibold">Standings Division Format:</label>
          <select
            value={divisionFormat}
            onChange={(e) => setDivisionFormat(e.target.value)}
            className="px-2 py-1.5 bg-[#161616] border border-[#262626] rounded text-sm text-white"
            data-testid="division-format-select"
          >
            <option value="1D">1D (single division)</option>
            <option value="2D">2D</option>
            <option value="3D">3D (fastest, +1.0s, +2.0s)</option>
            <option value="4D">4D (fastest, +0.5s, +1.0s, +2.0s)</option>
            <option value="5D">5D (0.5s uniform increments)</option>
          </select>
          <span className="text-[10px] text-[#9E9E9E]">Applies to ALL classes when rolling up standings.</span>
        </div>
        <div className="space-y-2 mb-3">
          {placings.map((p, i) => (
            <div key={i} className="grid grid-cols-[80px_1fr_36px] gap-2 items-center" data-testid={`placing-row-${i}`}>
              <input type="number" min="1" value={p.place} onChange={(e) => updatePlacing(i, 'place', e.target.value)} className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" placeholder="Place" data-testid={`placing-place-${i}`} />
              <input type="number" min="0" step="0.5" value={p.points} onChange={(e) => updatePlacing(i, 'points', e.target.value)} className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" placeholder="Points" data-testid={`placing-points-${i}`} />
              <button onClick={() => removePlacing(i)} className="p-1.5 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded" data-testid={`placing-remove-${i}`}><Trash size={14} weight="bold" /></button>
            </div>
          ))}
        </div>
        <button onClick={addPlacing} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[#0096FF] border border-[#0096FF]/30 rounded text-xs font-semibold hover:bg-[#0096FF]/10" data-testid="add-placing-btn"><Plus size={13} weight="bold" /> Add Placing</button>
      </section>

      {/* Members */}
      <section className="bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6 mb-8" data-testid="members-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold">Members <span className="text-[#9E9E9E] text-sm font-normal">({members.length})</span></h2>
          <label className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded text-xs font-bold hover:bg-[#0096FF]/25 cursor-pointer" data-testid="csv-upload-btn">
            <UploadSimple size={13} weight="bold" /> Upload CSV
            <input ref={csvRef} type="file" accept=".csv" onChange={handleCsvUpload} className="hidden" data-testid="csv-upload-input" />
          </label>
        </div>
        <p className="text-[#9E9E9E] text-[11px] mb-3">
          Riders <span className="text-[#34C759] font-semibold">do not need to have an account</span> on the site —
          just add their name and start date. Email &amp; phone are optional.
        </p>
        <p className="text-[#9E9E9E] text-[10px] mb-3">CSV columns: first_name, last_name, email, phone, membership_start_date (YYYY-MM-DD), external_id, notes.</p>
        {/* Add-member form: essentials row (name + start date) + collapsible optional info. */}
        <div className="grid grid-cols-[1fr_1fr_140px_36px] gap-2 mb-2 items-end">
          <input value={addForm.first_name} onChange={(e) => setAddForm({...addForm, first_name: e.target.value})} placeholder="First name *" className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" data-testid="add-member-first" />
          <input value={addForm.last_name} onChange={(e) => setAddForm({...addForm, last_name: e.target.value})} placeholder="Last name *" className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" data-testid="add-member-last" />
          <input type="date" value={addForm.membership_start_date} onChange={(e) => setAddForm({...addForm, membership_start_date: e.target.value})} className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" data-testid="add-member-startdate" title="Membership start date" />
          <button onClick={addMember} className="p-1.5 bg-[#34C759]/15 text-[#34C759] rounded hover:bg-[#34C759]/25" data-testid="add-member-submit" title="Add member"><Plus size={16} weight="bold" /></button>
        </div>
        <button
          type="button"
          onClick={() => setAddFormOptOpen((o) => !o)}
          className="inline-flex items-center gap-1 text-[10px] text-[#9E9E9E] hover:text-white mb-2"
          data-testid="toggle-optional-fields"
        >
          {addFormOptOpen ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
          Optional info (email, phone)
        </button>
        {addFormOptOpen && (
          <div className="grid grid-cols-[1fr_1fr] gap-2 mb-2" data-testid="optional-fields-row">
            <input value={addForm.email} onChange={(e) => setAddForm({...addForm, email: e.target.value})} placeholder="Email (optional)" className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" data-testid="add-member-email" />
            <input value={addForm.phone} onChange={(e) => setAddForm({...addForm, phone: e.target.value})} placeholder="Phone (optional)" className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm" data-testid="add-member-phone" />
          </div>
        )}
        <div className="mt-4 max-h-96 overflow-y-auto">
          {members.map((m) => (
            <div key={m.id} className="grid grid-cols-[2fr_1fr_1fr_140px_28px] gap-2 py-1.5 border-b border-[#262626] items-center text-sm" data-testid={`member-row-${m.id}`}>
              <span className="text-white">{m.first_name} {m.last_name}</span>
              <span className="text-[#9E9E9E] text-xs truncate">{m.email || '—'}</span>
              <span className="text-[#9E9E9E] text-xs">{m.phone || '—'}</span>
              <input type="date" defaultValue={m.membership_start_date} onBlur={(e) => e.target.value !== m.membership_start_date && updateStartDate(m.id, e.target.value)} className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-xs" data-testid={`member-startdate-${m.id}`} />
              <button onClick={() => deleteMember(m.id)} className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded" data-testid={`member-delete-${m.id}`}><Trash size={12} weight="bold" /></button>
            </div>
          ))}
          {members.length === 0 && <p className="text-[#555] text-sm text-center py-8">No members yet — add one above or upload a CSV.</p>}
        </div>
      </section>

      {/* Standings */}
      <section className="bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6" data-testid="standings-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h2 className="text-lg font-bold flex items-center gap-2"><Trophy size={18} weight="fill" className="text-[#FF9500]" /> Season Standings</h2>
          <div className="flex items-center gap-2">
            <input type="number" placeholder="Year (optional)" value={year} onChange={(e) => setYear(e.target.value)} className="px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-sm w-32" data-testid="standings-year" />
            <button onClick={loadStandings} className="px-3 py-1.5 bg-[#FF9500]/15 text-[#FF9500] border border-[#FF9500]/30 rounded text-xs font-bold hover:bg-[#FF9500]/25" data-testid="standings-refresh">Compute</button>
            {standings && (
              <button
                onClick={downloadStandingsPdf}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0096FF]/15 text-[#0096FF] border border-[#0096FF]/30 rounded text-xs font-bold hover:bg-[#0096FF]/25"
                data-testid="standings-pdf-btn"
              >
                <FilePdf size={13} weight="bold" /> Export PDF
              </button>
            )}
          </div>
        </div>
        {standings && (() => {
          const buckets = standings.buckets || [];
          const activeBucket = buckets.find((b) => b.division === selectedBucket) || buckets[0];
          const activeRows = activeBucket?.standings || [];
          const activeDivName = activeBucket?.division || '';
          return (
            <div>
              <p className="text-[#9E9E9E] text-xs mb-2">
                Shows counted: {standings.shows_counted} · Format: <span className="text-[#FF9500] font-semibold">{standings.division_format}</span>
              </p>
              {/* Tabs: one per division in the org's format. */}
              <div className="flex gap-1 mb-3 overflow-x-auto pb-1 border-b border-[#262626]" data-testid="standings-tabs">
                {buckets.map((b) => (
                  <button
                    key={b.division}
                    onClick={() => setSelectedBucket(b.division)}
                    className={`shrink-0 px-3 py-1.5 text-xs font-bold rounded-t border-b-2 transition-colors whitespace-nowrap ${
                      (selectedBucket || buckets[0]?.division) === b.division
                        ? 'text-[#FF9500] border-[#FF9500] bg-[#FF9500]/5'
                        : 'text-[#9E9E9E] border-transparent hover:text-white'
                    }`}
                    data-testid={`standings-tab-${b.division}`}
                  >
                    {b.division === 'OVERALL' ? 'Overall' : b.division}
                  </button>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[#9E9E9E] text-[10px] uppercase tracking-wider border-b border-[#262626]">
                      <th className="text-left py-2">Rank</th>
                      <th className="text-left py-2">Member</th>
                      <th className="text-right py-2">Points</th>
                      <th className="text-right py-2">Placings</th>
                      <th className="text-right py-2">Award</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeRows.map((s, i) => {
                      const rank = i + 1;
                      const isChamp = rank === 1;
                      const isReserve = rank === 2;
                      return (
                        <tr
                          key={s.member_id}
                          className={`border-b border-[#262626]/50 ${
                            isChamp ? 'bg-[#FFD700]/10' : isReserve ? 'bg-[#C0C0C0]/10' : ''
                          }`}
                          data-testid={`standing-row-${s.member_id}`}
                        >
                          <td className="py-2 text-[#9E9E9E]">#{rank}</td>
                          <td className="py-2 font-semibold">{s.first_name} {s.last_name}</td>
                          <td className="py-2 text-right text-[#FF9500] font-bold">{s.total_points}</td>
                          <td className="py-2 text-right text-[#9E9E9E] text-xs">{s.results.length}</td>
                          <td className="py-2 text-right">
                            {isChamp && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40 rounded text-[10px] font-bold uppercase tracking-wider" data-testid={`champion-badge-${s.member_id}`}>
                                <Crown size={11} weight="fill" /> {activeDivName} Champ
                              </span>
                            )}
                            {isReserve && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#C0C0C0]/20 text-[#D4D4D4] border border-[#C0C0C0]/40 rounded text-[10px] font-bold uppercase tracking-wider" data-testid={`reserve-badge-${s.member_id}`}>
                                <Medal size={11} weight="fill" /> {activeDivName} Reserve
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {activeRows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-[#555] py-6">
                          No qualifying results in the {activeDivName} yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}
        {!standings && <p className="text-[#555] text-sm text-center py-4">Click Compute to load standings.</p>}
      </section>
    </div>
  );
}
